import React, { useEffect, useState } from 'react';
import { X, Share, Plus, Download } from 'lucide-react';
import Logo from '@/components/Logo';

const DISMISS_KEY = 'poettik_pwa_dismissed';

// "Add to home screen" prompt. On Android/Chrome we hook into the native
// beforeinstallprompt event; on iOS Safari (which has no such event) we show a
// custom card explaining the Share → Add to Home Screen gesture. Mobile-only,
// dismissible, and remembers dismissal via localStorage.
const PWAInstallPrompt = () => {
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [visible, setVisible] = useState(false);
  const [isIOS, setIsIOS] = useState(false);

  useEffect(() => {
    if (typeof window === 'undefined') return undefined;
    if (localStorage.getItem(DISMISS_KEY)) return undefined;

    const standalone = window.matchMedia('(display-mode: standalone)').matches || window.navigator.standalone;
    if (standalone) return undefined;
    if (window.innerWidth >= 768) return undefined; // mobile only

    const ua = window.navigator.userAgent.toLowerCase();
    const ios = /iphone|ipad|ipod/.test(ua) && !/crios|fxios/.test(ua);

    if (ios) {
      setIsIOS(true);
      const t = setTimeout(() => setVisible(true), 2500);
      return () => clearTimeout(t);
    }

    const handler = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
      setVisible(true);
    };
    window.addEventListener('beforeinstallprompt', handler);
    return () => window.removeEventListener('beforeinstallprompt', handler);
  }, []);

  const dismiss = () => {
    setVisible(false);
    try { localStorage.setItem(DISMISS_KEY, '1'); } catch (_) {}
  };

  const install = async () => {
    if (!deferredPrompt) return;
    deferredPrompt.prompt();
    try { await deferredPrompt.userChoice; } catch (_) {}
    setDeferredPrompt(null);
    dismiss();
  };

  if (!visible) return null;

  return (
    <div
      className="md:hidden fixed left-3 right-3 z-[95] bottom-[calc(4.5rem+env(safe-area-inset-bottom))]"
      data-testid="pwa-install-prompt"
    >
      <div className="relative rounded-2xl border border-[#E6DED1] bg-[#FFFDF9] shadow-[0_16px_40px_rgba(70,45,20,0.16)] p-4">
        <button
          onClick={dismiss}
          aria-label="Dismiss"
          data-testid="pwa-install-dismiss"
          className="absolute top-3 right-3 p-1 rounded-lg text-[#6F675D] hover:bg-[#EFE7DB]"
        >
          <X className="w-4 h-4" />
        </button>

        <div className="flex items-start gap-3 pr-6">
          <span className="shrink-0 w-11 h-11 rounded-xl bg-[#FBE4EE] flex items-center justify-center">
            <Logo size="sm" />
          </span>
          <div className="min-w-0">
            <p className="font-display text-base leading-tight text-[#1F1B16]">Add Poettik to your home screen</p>
            {isIOS ? (
              <p className="text-xs text-[#6F675D] leading-snug mt-1">
                Tap <Share className="inline w-3.5 h-3.5 -mt-0.5 mx-0.5" /> Share, then
                <span className="whitespace-nowrap"> “Add to Home Screen” <Plus className="inline w-3.5 h-3.5 -mt-0.5" /></span>
                for an app-like experience.
              </p>
            ) : (
              <p className="text-xs text-[#6F675D] leading-snug mt-1">
                Install the app for faster access and a full-screen, app-like experience.
              </p>
            )}
          </div>
        </div>

        {!isIOS && (
          <div className="mt-3 flex gap-2">
            <button
              onClick={install}
              data-testid="pwa-install-accept"
              className="flex-1 inline-flex items-center justify-center gap-1.5 h-10 rounded-xl bg-[#1F1B16] text-[#FFFDF9] text-sm font-medium active:scale-[0.98] transition-transform"
            >
              <Download className="w-4 h-4" />
              Install app
            </button>
            <button
              onClick={dismiss}
              className="h-10 px-4 rounded-xl border border-[#E6DED1] text-sm text-[#5E574E]"
            >
              Not now
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default PWAInstallPrompt;
